/**
 * useRoutineDraft — the shared draft store for one routine editor instance.
 *
 * This is the single source of truth for the editor's {@link WorkflowDraft}, lifted to the
 * route (P12 §6.2) so BOTH the editor pane and the RoutineBuilder chat pane write to the
 * same draft. Two write paths:
 *   - `applyUserEdit(partial, editedStep?)` — a human field edit (the editor is human-editable once
 *     the agent is idle; also used by the name/schedule chrome).
 *   - `applyAgentPatch(patch)` — a `routine_draft` stage patch from the agent, flattened into
 *     the view-model draft via the shared mappers.
 *
 * It also owns:
 *   - load/hydrate from `workflow.get` for `/workflows/:id` (blank for `/workflows/new`),
 *   - `snapshot()` — the persisted-vocabulary wire snapshot the chat sends the agent at turn
 *     start (built via the shared mappers, byte-identical to what the backend draft stores),
 *   - the **save-state** chip (§L11): `editing → unsaved → saved`,
 *   - the active-edit "staged, not live yet" flag — editing an ACTIVE routine does NOT
 *     auto-apply; the live routine keeps running the saved version until an explicit Save.
 *
 * The agent-patch reducer (mapping every `RoutineDraftPatch` stage → flat draft + the touched
 * {@link StepKey}) is the canonical {@link applyRoutineDraftPatch} in `../draft-reducer` (T-FE2);
 * `applyAgentPatch` below delegates to it and uses the returned `editedStep` for staleness.
 */
import type { RoutineDraftPatch, RoutineDraftSnapshot, WorkflowStatus } from '@server/types';
import type { WorkflowAccountRepairView } from '@server/warehouse-repair-state';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { formatSql } from '@/_shared/lib/sql';

import { applyRoutineDraftPatch } from '../draft-reducer';
import { getLocalTimezone } from '../lib/timezones';
import type { RoutineField } from '../lib/validation';
import { conditionToConfig, graphToDraft, scheduleToTrigger } from '../mappers';
import type { StepKey, WorkflowDraft } from '../types';

// ─── Blank draft ─────────────────────────────────────────────────────────────

export const BLANK_DRAFT: WorkflowDraft = {
  name: 'New routine',
  description: '',
  schedule: { cadence: 'daily', time: '09:00', timezone: 'UTC' },
  warehouseId: '',
  sql: '',
  condition: null,
  channel: '',
  template: '',
};

/** A blank draft for a brand-new routine — like {@link BLANK_DRAFT} but anchored to the local zone (NP-B). */
function newBlankDraft(): WorkflowDraft {
  return { ...BLANK_DRAFT, schedule: { ...BLANK_DRAFT.schedule, timezone: getLocalTimezone() } };
}

/** Step chain order, used to propagate staleness downstream when a step is edited. */
const STEP_ORDER: readonly StepKey[] = ['query', 'condition', 'slack'];

// ─── Save-state ────────────────────────────────────────────────────────────────

/**
 * The save-state chip value (§L11):
 *   - `editing`  — pristine since load/save; nothing to persist yet.
 *   - `unsaved`  — the draft has edits not yet persisted (auto-save target for drafts;
 *     for an ACTIVE routine this means staged edits that are NOT live yet).
 *   - `saved`    — the draft matches the last persisted version.
 */
export type RoutineSaveState = 'editing' | 'unsaved' | 'saved';

// ─── Store ─────────────────────────────────────────────────────────────────────

export interface RoutineDraftStore {
  /**
   * The on-screen draft, plus the query node's connection stamp when one is set.
   *
   * The stamp is not a user-editable FIELD — no step renders it — but it is draft state: it is read
   * off a loaded graph and, since AI-1370, minted when the human picks a warehouse, and it has to
   * survive to `draftToGraph` either way. The state has always carried it at runtime; typing it here
   * is what lets the picker set it without a cast.
   */
  draft: WorkflowDraft;
  /** Apply a human field edit. `editedStep` propagates stale to that step + downstream. */
  applyUserEdit: (patch: Partial<WorkflowDraft>, editedStep?: StepKey) => void;
  /**
   * Apply a name/description rename to the on-screen draft WITHOUT touching save-state (ACT2-18). Unlike
   * {@link applyUserEdit}, this never flips the chip to `unsaved` — a rename is persisted immediately by
   * the actions layer's own mutation, so it must not dirty a clean routine nor clear a dirty one.
   */
  applyRenameLocal: (name: string, description: string) => void;
  /** Apply an agent `routine_draft` stage patch (flattened into the view-model draft). */
  applyAgentPatch: (patch: RoutineDraftPatch) => void;
  /** The persisted-vocabulary snapshot the chat sends the agent at turn start. */
  snapshot: () => RoutineDraftSnapshot;
  staleSteps: ReadonlySet<StepKey>;
  setStaleSteps: React.Dispatch<React.SetStateAction<ReadonlySet<StepKey>>>;
  /** Per-step counter that increments only on agent patches — keys the editor's highlight flash. */
  agentNonces: Record<StepKey, number>;
  /** Fields the user has touched (blurred) — a touched field may show its validation error. */
  touchedFields: ReadonlySet<RoutineField>;
  /** True once a Save/Preview was attempted — every field's error becomes visible (form-submit gate). */
  submitAttempted: boolean;
  /** Mark a field touched (on blur) so its validation error may show. */
  markTouched: (field: RoutineField) => void;
  /** Reveal all field errors — the actions layer calls this on a blocked Save/Preview. */
  markSubmitAttempted: () => void;
  /** Save-state chip value (§L11). */
  saveState: RoutineSaveState;
  /**
   * True when a durable, server-persisted working copy exists for this routine (Issue 11b) — i.e. the
   * editor is showing unsaved edits that survive reload/refetch/browser-close until Save (promote) or
   * Discard. Drives the "Unsaved draft" indicator + the "Discard draft" affordance. Always false for an
   * unsaved brand-new routine (working copies exist only for already-published routines).
   */
  hasWorkingCopy: boolean;
  /** Called by the actions layer after a successful autosave — flags the durable working copy present. */
  markDraftPersisted: () => void;
  /**
   * Discard the working copy locally: revert the on-screen draft to the published graph and clear the
   * unsaved/stale state. The actions layer calls this after the server `discardDraft` succeeds.
   */
  discardWorkingCopy: () => void;
  /**
   * True when an ACTIVE routine has unsaved (staged) edits — the live routine is still running
   * the saved version until an explicit Save (§L11). Always false for draft/paused routines and
   * when there are no unsaved edits.
   */
  isActiveEdit: boolean;
  /** Mark the current draft as persisted (called by the actions layer after a successful save). */
  markSaved: () => void;
  isLoading: boolean;
  loadError: Error | null;
  /** Re-run the `workflow.get` query — drives the load-error Retry (M2). */
  refetch: () => void;
  isRefetching: boolean;
  workflowId: string | undefined;
  workflowStatus: WorkflowStatus | undefined;
  /** ACT2-1286: per-step needs-repair state and the repair pause. Undefined until loaded / for a new routine. */
  accountRepair: WorkflowAccountRepairView | undefined;
}

/**
 * Build the persisted-vocabulary snapshot from the flat editor draft.
 * `kind` is auto-managed (condition present ⇒ alert), mirroring the backend draft model.
 */
function toSnapshot(draft: WorkflowDraft): RoutineDraftSnapshot {
  const { condition } = draft;
  return {
    name: draft.name || undefined,
    description: draft.description || undefined,
    kind: condition ? 'alert' : 'notification',
    trigger: scheduleToTrigger(draft.schedule),
    query: draft.warehouseId || draft.sql ? { sql: draft.sql, warehouseId: draft.warehouseId } : undefined,
    // The editor `op` is already the ConditionConfig op enum; `value` parses to the numeric rhs.
    logic: condition ? { lhs: 'row_count', op: condition.op, rhs: conditionToConfig(condition).rhs ?? 0 } : undefined,
    destination:
      draft.channel || draft.template ? { kind: 'slack', channel: draft.channel, template: draft.template } : undefined,
  };
}

export function useRoutineDraft(workflowId: string | undefined, initialDraft?: WorkflowDraft): RoutineDraftStore {
  const orpc = useOrpc();

  // ── Load ────────────────────────────────────────────────────────────────
  const getQuery = useQuery({
    ...orpc.workflow.get.queryOptions({ input: { id: workflowId ?? '' } }),
    enabled: workflowId !== undefined,
  });

  // Local draft state. Seed from initialDraft (Storybook/tests), loaded data, or a blank draft (a
  // brand-new routine defaults its schedule to the viewer's local timezone — NP-B).
  const [draft, setDraft] = React.useState<WorkflowDraft>(
    () => initialDraft ?? (workflowId === undefined ? newBlankDraft() : BLANK_DRAFT),
  );
  const [staleSteps, setStaleSteps] = React.useState<ReadonlySet<StepKey>>(new Set());
  const [saveState, setSaveState] = React.useState<RoutineSaveState>('editing');
  const [hasWorkingCopy, setHasWorkingCopy] = React.useState(false);
  // Per-step counter bumped ONLY when the agent patches that step — drives the editor's highlight
  // flash (§6.5) so it fires on agent fills, not on the human's own edits.
  const [agentNonces, setAgentNonces] = React.useState<Record<StepKey, number>>({ query: 0, condition: 0, slack: 0 });

  // Form-validation display state: a required field's error shows only once it's TOUCHED (blurred) or a
  // Save/Preview was ATTEMPTED — so a blank new routine isn't lit up with errors on load. Both reset
  // when a routine (re)loads or the draft is discarded.
  const [touchedFields, setTouchedFields] = React.useState<ReadonlySet<RoutineField>>(new Set());
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  // Imperative draft mirror so snapshot() can read the latest value without a re-render
  // subscription (the chat envelope reads it at turn start, off the render path). Updated in an
  // effect — not during render — so the ref tracks the committed draft.
  const draftRef = React.useRef(draft);
  React.useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  // Sync draft from server when it loads (only on initial load, not on refetch after save). When a
  // durable working copy exists (Issue 11b) we seed from IT, not the published graph — so a reload /
  // post-turn refetch re-loads the user's own unsaved edits instead of silently reverting them. The
  // `loadedId` guard keeps a refetch (same id) from re-seeding, so an in-flight edit is never clobbered.
  const loadedId = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    const wf = getQuery.data?.workflow;
    if (!wf) return;
    if (loadedId.current === wf.id) return; // already applied
    loadedId.current = wf.id;
    // Seed from the durable working copy when present, else the published row. Pristine either way
    // ('editing'): no immediate re-autosave — the "Unsaved draft" indicator is driven by
    // `hasWorkingCopy`, and Save (promote) stays enabled.
    const wc = getQuery.data?.workingCopy;
    const source = wc ?? wf;
    // Pretty-print the loaded SQL so the editor never shows the single-line form a routine may have
    // been saved with (or that the agent wrote). Pristine: formatting happens before the draft is
    // set, so it doesn't flip the save-state to unsaved.
    const loaded = graphToDraft(source.name, source.description ?? '', source.trigger, { nodes: source.graph.nodes });
    setDraft({ ...loaded, sql: formatSql(loaded.sql) });
    setHasWorkingCopy(!!wc);
    setSaveState('editing');
    setTouchedFields(new Set());
    setSubmitAttempted(false);
  }, [getQuery.data?.workflow, getQuery.data?.workingCopy]);

  // ACT2-1286: a confirmed repair rewrites the PUBLISHED query step's account (its `warehouseId` too), but
  // the seed effect above never re-seeds on a refetch. Left alone, the editor would keep showing the
  // removed account and the next autosave or Save would send it back, undoing the repair. So when the
  // published step's account changes underneath an editor that still shows the old one, carry the
  // editor along. A user's own pick (draft differs from the old value) is left as it is. Not an edit:
  // the save state does not change, but the query step and everything after it are marked stale, since
  // a Preview run on the removed account says nothing about the repaired one.
  //
  // The remembered value is keyed by routine: this editor instance is reused across /workflows/:id
  // navigations, and B's published account must never be compared with A's (that would overwrite a
  // working copy's own pick on B with B's published account).
  const published = React.useMemo(() => {
    const wf = getQuery.data?.workflow;
    if (!wf) return undefined;
    const { warehouseId } = graphToDraft(wf.name, wf.description ?? '', wf.trigger, { nodes: wf.graph.nodes });
    // A repair can also keep the `warehouseId` and rewrite only the step's account hint, so the account
    // the step runs on is tracked too: the editor shows the same warehouse, but its Preview is stale.
    const query = wf.graph.nodes.find((n) => n.kind === 'query');
    const config = (query?.config ?? {}) as { accountKey?: string | null; lastAccountId?: string | null };
    const account = `${config.accountKey ?? ''}|${config.lastAccountId ?? ''}`;
    return { workflowId: wf.id, warehouseId, account };
  }, [getQuery.data?.workflow]);
  const lastPublished = React.useRef<{ workflowId: string; warehouseId: string; account: string } | undefined>(
    undefined,
  );
  React.useEffect(() => {
    if (published === undefined) return;
    const previous = lastPublished.current;
    lastPublished.current = published;
    if (previous?.workflowId !== published.workflowId) return;
    const warehouseMoved = previous.warehouseId !== published.warehouseId;
    if (!warehouseMoved && previous.account === published.account) return;

    // The editor follows only while it still shows the published step's previous warehouse.
    const follows = draftRef.current.warehouseId === previous.warehouseId;
    if (warehouseMoved) {
      setDraft((d) => (d.warehouseId === previous.warehouseId ? { ...d, warehouseId: published.warehouseId } : d));
    }
    if (follows) setStaleSteps(new Set(STEP_ORDER));
  }, [published]);

  // Cross-routine leak guard. This editor instance is REUSED when the route id changes (react-router
  // keeps `WorkflowDetail` mounted for /workflows/:id → /workflows/:other), so `draft`/`saveState`
  // briefly still hold routine A while `workflowId` is already B and B's get hasn't settled. Reset to a
  // pristine blank on that transition so the autosave effect (keyed on `workflowId` + `draft`) can't
  // write A's graph into B's working copy; the seed effect above re-hydrates once B's data arrives.
  // The `loadedId === undefined` early-return skips this on the initial mount and the new→saved
  // navigation (undefined → id), preserving the in-memory draft handed off on first Save (§4.5).
  React.useEffect(() => {
    if (loadedId.current === undefined) return;
    if (loadedId.current === workflowId) return;
    lastPublished.current = undefined;
    setDraft(workflowId === undefined ? newBlankDraft() : BLANK_DRAFT);
    setStaleSteps(new Set());
    setSaveState('editing');
    setHasWorkingCopy(false);
    setTouchedFields(new Set());
    setSubmitAttempted(false);
  }, [workflowId]);

  // ── Write paths ───────────────────────────────────────────────────────────
  const applyUserEdit = React.useCallback((patch: Partial<WorkflowDraft>, editedStep?: StepKey): void => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setSaveState('unsaved');
    if (editedStep === undefined) return;
    // Mark edited step + every downstream step stale.
    const from = STEP_ORDER.indexOf(editedStep);
    if (from === -1) return;
    setStaleSteps((prev) => {
      const next = new Set(prev);
      STEP_ORDER.slice(from).forEach((s) => next.add(s));
      return next;
    });
  }, []);

  // Rename edit — updates only name/description and deliberately leaves `saveState` alone (the actions
  // layer persists the rename on its own; it must not dirty a clean routine or mask other pending edits).
  const applyRenameLocal = React.useCallback((name: string, description: string): void => {
    setDraft((prev) => ({ ...prev, name, description }));
  }, []);

  const applyAgentPatch = React.useCallback((patch: RoutineDraftPatch): void => {
    // Read `editedStep` OUTSIDE the updater: React only runs a setState updater synchronously on the
    // eager-state path, which is gone once the fiber has pending updates (this runs from the chat SSE
    // handler, so it always does). Reading it off the updater would leave it undefined and silently
    // skip the highlight flash + staleness propagation below. `editedStep` is a pure function of
    // `patch.stage` (never of the draft), so the draft passed here is immaterial; the actual draft
    // still goes through the functional updater so rapid back-to-back patches chain correctly.
    const { editedStep } = applyRoutineDraftPatch(draftRef.current, patch);
    setDraft((prev) => {
      const result = applyRoutineDraftPatch(prev, patch);
      // Pretty-print the SQL the moment the agent fills it — the agent emits single-line SQL, and
      // the editor should show it formatted without waiting for a Format click or a reload.
      if (result.editedStep === 'query') return { ...result.draft, sql: formatSql(result.draft.sql) };
      return result.draft;
    });
    setSaveState('unsaved');
    if (editedStep === undefined) return;
    const step = editedStep;
    setAgentNonces((prev) => ({ ...prev, [step]: prev[step] + 1 }));
    const from = STEP_ORDER.indexOf(step);
    if (from === -1) return;
    setStaleSteps((prev) => {
      const next = new Set(prev);
      STEP_ORDER.slice(from).forEach((s) => next.add(s));
      return next;
    });
  }, []);

  const snapshot = React.useCallback((): RoutineDraftSnapshot => toSnapshot(draftRef.current), []);

  // Save promotes the working copy → published and deletes it server-side, so clear the durable-draft
  // flag alongside the chip (Issue 11b).
  const markSaved = React.useCallback((): void => {
    setSaveState('saved');
    setHasWorkingCopy(false);
  }, []);

  const markDraftPersisted = React.useCallback((): void => setHasWorkingCopy(true), []);

  // Mark a field touched (on blur) so its error may show; a Save/Preview attempt shows every field's.
  const markTouched = React.useCallback((field: RoutineField): void => {
    setTouchedFields((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));
  }, []);
  const markSubmitAttempted = React.useCallback((): void => setSubmitAttempted(true), []);

  const discardWorkingCopy = React.useCallback((): void => {
    const wf = getQuery.data?.workflow;
    if (wf) {
      const published = graphToDraft(wf.name, wf.description ?? '', wf.trigger, { nodes: wf.graph.nodes });
      setDraft({ ...published, sql: formatSql(published.sql) });
    }
    setHasWorkingCopy(false);
    setStaleSteps(new Set());
    setSaveState('editing');
    setTouchedFields(new Set());
    setSubmitAttempted(false);
  }, [getQuery.data?.workflow]);

  const workflowStatus = getQuery.data?.workflow?.status;
  // §L11: editing an ACTIVE (live, running) routine stages edits — the live routine keeps
  // running the previously-saved version until an explicit Save. The warning chip fires only
  // once there are unsaved edits on a live routine. Draft/paused routines are not live, so
  // their edits auto-save normally (no staged-vs-live trap).
  const isActiveEdit = saveState === 'unsaved' && workflowStatus === 'active';

  return {
    draft,
    applyUserEdit,
    applyRenameLocal,
    applyAgentPatch,
    snapshot,
    staleSteps,
    setStaleSteps,
    agentNonces,
    touchedFields,
    submitAttempted,
    markTouched,
    markSubmitAttempted,
    saveState,
    isActiveEdit,
    markSaved,
    hasWorkingCopy,
    markDraftPersisted,
    discardWorkingCopy,
    isLoading: workflowId !== undefined && getQuery.isLoading,
    // Only the INITIAL load failure (no data ever fetched) — `isLoadingError`, not the raw `error`.
    // A background-refetch failure keeps the last-good data, so surfacing it must NOT blank the editor.
    loadError: workflowId !== undefined && getQuery.isLoadingError ? getQuery.error : null,
    refetch: () => void getQuery.refetch(),
    isRefetching: getQuery.isRefetching,
    workflowId,
    workflowStatus,
    accountRepair: getQuery.data?.accountRepair,
  };
}
